import { Fragment } from 'react';

import { Text } from '@change/ds-core-ui/Text';
import { cn } from '@change/ds-utils';

import { translationKey } from '@change-corgi/core/i18n';
import { TypedTranslate } from '@change-corgi/core-react/i18n';

// Copy shown while an AI petition image is generating (SAP /edit, /preview): the two lines
// rotate continuously, one holding while the other stays hidden, then softly crossfading —
// each line gets ~4s visible before handing off. See the animate-copy-loop token in index.css
// for the exact transition timing (measured against the reference animation).
//
// The second line's delay is negative, not positive. copy-loop's 0% keyframe is opacity:1 with
// no fill-mode, so a positive delay would hold at the base opacity-0 style until the delay
// elapses, then jump straight to the keyframe's opacity:1 — a pop, not a fade. A negative delay
// instead starts the animation already in progress (evaluated continuously from render), with
// the exact same steady-state phase: -4.2s and +4.2s are equivalent modulo the 8.4s period since
// 4.2s is exactly half of it, so this only changes how the very first cycle starts, not the
// timing verified against the reference.
const COPY_SEQUENCE = [
	{
		key: translationKey('corgi.start_a_petition.common.image_generation_loading.copy_1'),
		className: 'animate-copy-loop motion-reduce:static motion-reduce:animate-none',
		delay: 0,
	},
	{
		key: translationKey('corgi.start_a_petition.common.image_generation_loading.copy_2'),
		className: 'opacity-0 animate-copy-loop motion-reduce:static motion-reduce:animate-none motion-reduce:opacity-100',
		delay: -4.2,
	},
] as const;

// Matches the provided reference exactly (measured from the reference SVG's own sparkle
// paths): one larger sparkle on the left, vertically centered, with two equal-sized smaller
// sparkles stacked at its top-right and bottom-right — each independently twinkling (fade +
// scale) rather than a single shimmer sweep. One shared 24x24 viewBox (matching the h-6 w-6
// container) with a `<g transform>` per sparkle reproduces the old per-sparkle SVG sizing
// (scale = box size / 24) and position (translate = box offset within the 24x24 container) —
// the positioning transform lives on the `<g>` rather than the `<path>` itself because the
// twinkle animation below also drives `transform` (scale) on the path; CSS animations replace
// the full computed transform value rather than composing with it, so a static transform on
// the same element the animation targets would be clobbered for the animation's duration.
const SPARKLES = [
	{ transform: 'translate(0 4) scale(0.667)', delay: 0 }, // top-1 left-0, 16/24
	{ transform: 'translate(14 0) scale(0.417)', delay: 0.6 }, // top-0 right-0, 10/24
	{ transform: 'translate(14 14) scale(0.417)', delay: 1.2 }, // bottom-0 right-0, 10/24
] as const;

export const ImageGenerationLoading: React.FC = () => (
	<div className="flex items-center gap-2" data-qa="image-generation-loading">
		<svg viewBox="0 0 24 24" aria-hidden="true" className="h-6 w-6 shrink-0">
			{SPARKLES.map(({ transform, delay }) => (
				<g key={delay} transform={transform}>
					<path
						d="M12 2 L14.5 9.5 L22 12 L14.5 14.5 L12 22 L9.5 14.5 L2 12 L9.5 9.5 Z"
						fill="currentColor"
						className="animate-sparkle-twinkle motion-reduce:animate-none motion-reduce:opacity-100"
						style={{ animationDelay: `${delay}s`, transformBox: 'fill-box', transformOrigin: 'center' }}
					/>
				</g>
			))}
		</svg>

		{/* Absolutely-positioned copy spans don't contribute to intrinsic width (needed so they can
		    crossfade in place), so this wrapper needs an explicit width rather than flex-1/min-w-0 —
		    otherwise it collapses to 0 in a shrink-to-fit flex row (see git history for that bug).
		    flex-shrink doesn't help here since every ancestor between this component and the page's
		    rounded card wrapper is overflow-visible and shrink-to-fit, so nothing forces a squeeze —
		    below `md` (where the Hero uses raw px-4 padding instead of grid columns) this is sized
		    directly off the viewport instead: 100vw minus 6rem of fixed overhead measured from the
		    live DOM (2rem the page's own rounded-card wrapper padding, 2rem the Hero's px-4, 2rem this
		    component's own sparkle+gap), capped at the `md:w-80` preferred size confirmed to fit from
		    `md` up. `truncate` on the Text makes any remaining clipping (narrower screens than tested,
		    or long translated strings) an explicit ellipsis instead of a hard cut. */}
		<div
			aria-hidden="true"
			className="relative h-6 w-[calc(100vw-6rem)] max-w-80 min-w-0 overflow-hidden text-left motion-reduce:h-auto motion-reduce:overflow-visible md:w-80"
		>
			{COPY_SEQUENCE.map(({ key, className, delay }) => (
				<span
					key={key as unknown as string}
					className={cn('absolute inset-0 flex items-center will-change-[opacity]', className)}
					style={{ animationDelay: `${delay}s` }}
				>
					<Text className="min-w-0 truncate">
						<TypedTranslate value={key} />
					</Text>
				</span>
			))}
		</div>

		{/* The animated copy above is aria-hidden since both lines exist in the DOM at once (only
		    opacity distinguishes which is "shown", and opacity doesn't remove a node from the
		    accessibility tree) — screen readers would otherwise read both, duplicated and out of
		    sync with what's visible. This announces both lines once, statically, instead. */}
		{/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- not fixed to avoid regression risk from an automated markup rewrite; needs manual accessibility review */}
		<span className="sr-only" role="status" aria-live="polite" aria-atomic>
			{COPY_SEQUENCE.map(({ key }, index) => (
				<Fragment key={key as unknown as string}>
					{index > 0 && '. '}
					<Text>
						<TypedTranslate value={key} />
					</Text>
				</Fragment>
			))}
		</span>
	</div>
);
